/* eslint-disable prettier/prettier */
/* eslint-disable indent */

'use client';

import { ComponentProps, useEffect, useState } from 'react';
import {
  AxeIcon,
  BellIcon,
  BriefcaseBusinessIcon,
  LayoutDashboardIcon,
  ReceiptTextIcon,
  ScaleIcon,
  CircleUserIcon,
  LucideIcon as TLucideIcon,
} from 'lucide-react';

import { useRTL, useTranslations, useRouter } from '@northladder/i18n';
import { useBuyerAppConfig, useAuthStore } from '@northladder/services';

import { LanguageSwitchWidget } from '../../components';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarRail,
  SidebarSeparator,
  useSidebar,
} from '../../ui/sidebar-cn';
import { ThemeControlWidget } from '../../theme';

import { AppSidebarLogo } from './AppSidebarLogo';
import { NavMain } from './NavMain';
import { NavProjects } from './NavProjects';
import { NavUserWidget } from './NavUserWidget';

interface IMenuSubItem {
  isDisabled?: boolean;
  isHidden?: boolean;
  title: string;
  url: string;
}

interface IMenuItemBaseOptions {
  icon: TLucideIcon;
  isActive?: boolean;
  items: IMenuSubItem[];
  title: string;
  url: string;
}

/**
 * -----------------------------------------------------------------------------
 * The application sidebar building block
 */
export function AppSidebar({ ...props }: ComponentProps<typeof Sidebar>) {
  const t = useTranslations('AppDashboardSidebar');
  const { isRTL } = useRTL();
  const buyerAppConfig = useBuyerAppConfig();
  const {
    accountStatements,
    allBids,
    allDeals,
    allRequests,
    allTransactions,
    bidEfficiency,
    bidsSection,
    dashboardRecentActivity,
    dashboardSection,
    dashboardStatistics,
    dealsDelivered,
    dealsInTransit,
    dealsSection,
    gradeReviewHelp,
    gradeReviewSection,
    myProfile,
    myUploads,
    notifications,
    requestAReview,
    stagedBids,
    stagedBidsReport,
    transactionsInTransit,
  } = buyerAppConfig.sidebarFeature;

  const { open: isOpen } = useSidebar();
  const { replace } = useRouter();
  const [accountId, setAccountId] = useState('');
  const { activeSession } = useAuthStore();
  const accountIdIndex = activeSession?.index || '';
  const isTransactionsSectionVisible = accountStatements.isVisible;

  useEffect(() => {
    if (accountIdIndex) {
      setAccountId(`${accountIdIndex}`);
    }
  }, [accountIdIndex]);

  const transactionItem: IMenuItemBaseOptions[] = isTransactionsSectionVisible
    ? [
      {
        icon: ReceiptTextIcon,
        title: t('transactions'),
        url: `/${accountId}/account-statement`,
        items: [
          {
            title: t('allTransactions'),
            url: `/${accountId}/account-statement`,
            isDisabled: !allTransactions.isEnabled,
            isHidden: !allTransactions.isVisible,
          },
          {
            title: t('inTransit'),
            url: `/${accountId}/transactions/in-transit`,
            isDisabled: !transactionsInTransit.isEnabled,
            isHidden: !transactionsInTransit.isVisible,
          },
          {
            title: t('accountStatements'),
            url: `/${accountId}/transactions/account-statements`,
            isDisabled: !transactionsInTransit.isEnabled,
            isHidden: !transactionsInTransit.isVisible,
          },
        ],
      },
    ]
    : [];

  const gradeReviewItem: IMenuItemBaseOptions[] = gradeReviewSection.isVisible
    ? [
      {
        icon: ScaleIcon,
        title: t('gradeReviewRequests'),
        url: `/${accountId}/grade-review-requests`,
        items: [
          {
            title: t('allRequests'),
            url: `/${accountId}/grade-review-requests`,
            isDisabled: !allRequests.isEnabled,
            isHidden: !allRequests.isVisible,
          },
          {
            title: t('requestAReview'),
            url: `/${accountId}/grade-review-requests/new`,
            isDisabled: !requestAReview.isEnabled,
            isHidden: !requestAReview.isVisible,
          },
          {
            title: t('help'),
            url: `/${accountId}/articles/grade-review-requests/help`,
            isDisabled: !gradeReviewHelp.isEnabled,
            isHidden: !gradeReviewHelp.isVisible,
          },
        ],
      },
    ]
    : [];

  const appSidebarNavOptions: IMenuItemBaseOptions[] = [
    ...(dashboardSection.isVisible
      ? [
        {
          icon: LayoutDashboardIcon,
          isActive: true,
          title: t('dashboard'),
          url: '/dashboard',
          items: [
            {
              title: t('statistics'),
              url: `/${accountId}/dashboard/statistics`,
              isDisabled: !dashboardStatistics.isEnabled,
              isHidden: !dashboardStatistics.isVisible,
            },
            {
              title: t('recentActivity'),
              url: `/${accountId}/dashboard/recent-activity`,
              isDisabled: !dashboardRecentActivity.isEnabled,
              isHidden: !dashboardRecentActivity.isVisible,
            },
          ],
        },
      ]
      : []),

    ...(bidsSection.isVisible
      ? [
        {
          icon: AxeIcon,
          title: t('bids'),
          url: `/${accountId}/home`,
          items: [
            {
              title: t('liveBids'),
              url: `/${accountId}/bids`,
              isDisabled: !allBids.isEnabled,
              isHidden: !allBids.isVisible,
            },
            {
              title: t('stagedBids'),
              url: `/${accountId}/bids/bids-staged`,
              isDisabled: !stagedBids.isEnabled,
              isHidden: !stagedBids.isVisible,
            },
            {
              title: t('myUploads'),
              url: `/${accountId}/bids/uploaded-bids`,
              isDisabled: !myUploads.isEnabled,
              isHidden: !myUploads.isVisible,
            },
            {
              title: t('stagedBidsReport'),
              url: `/${accountId}/bids/staged-bids-report`,
              isDisabled: !stagedBidsReport.isEnabled,
              isHidden: !stagedBidsReport.isVisible,
            },
            {
              title: t('bidDashboard'),
              url: `/${accountId}/bids/bids-efficiency`,
              isDisabled: !bidEfficiency.isEnabled,
              isHidden: !bidEfficiency.isVisible,
            },
          ],
        },
      ]
      : []),

    ...(dealsSection.isVisible
      ? [
        {
          icon: BriefcaseBusinessIcon,
          title: t('deals'),
          url: `/${accountId}/deals`,
          items: [
            {
              title: t('allDeals'),
              url: `/${accountId}/deals`,
              isDisabled: !allDeals.isEnabled,
              isHidden: !allDeals.isVisible,
            },
            {
              title: t('dealsInTransit'),
              url: `/${accountId}/deals?status=isInTransit`,
              isDisabled: !dealsInTransit.isEnabled,
              isHidden: !dealsInTransit.isVisible,
            },
            {
              title: t('dealsDelivered'),
              url: `/${accountId}/deals?status=isDelivered`,
              isDisabled: !dealsDelivered.isEnabled,
              isHidden: !dealsDelivered.isVisible,
            },
          ],
        },
      ]
      : []),

    ...transactionItem,
    ...gradeReviewItem,
  ];

  const extraNavItems = [
    ...(notifications.isEnabled
      ? [
        {
          icon: BellIcon,
          isDisabled: !notifications.isEnabled,
          name: t('notifications'),
          url: `/${accountId}/notifications`,
        },
      ]
      : []),
    ...(myProfile.isVisible
      ? [
        {
          icon: CircleUserIcon,
          isDisabled: !myProfile.isEnabled,
          name: t('myProfile'),
          url: `/${accountId}/settings/profile`,
        },
      ]
      : []),
  ];

  const handleNavToProfile = () => {
    replace(`/${accountId}/settings/profile`);
  };

  return (
    <Sidebar
      collapsible="icon"
      side={isRTL ? 'right' : 'left'}
      variant={isOpen ? undefined : 'floating'}
      {...props}
    >
      <SidebarHeader>
        <AppSidebarLogo />
      </SidebarHeader>

      <SidebarSeparator className="!mx-0 mt-2 opacity-20" />

      <SidebarContent>
        <NavMain items={appSidebarNavOptions} title={t('platform')} />

        <SidebarSeparator className="!mx-0 opacity-20" />

        {extraNavItems.length > 0 ? (
          <NavProjects projects={extraNavItems} title={t('settings')} />
        ) : null}
      </SidebarContent>

      <SidebarFooter>
        <SidebarSeparator className="!mx-0 mt-2 opacity-20" />
        <LanguageSwitchWidget />
        <ThemeControlWidget />
        <SidebarSeparator className="!mx-0 mt-2 opacity-20" />

        <NavUserWidget onNavToProfile={handleNavToProfile} />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
